import TaskResultLimitPanel from "Areas/Documents/documentDetails/taskResultLimitPanel";
import AddToSummaryReport, { NEW_SUMMARY_REPORT_OBJECT } from "Areas/SummaryReports/addToSummaryReport";
import { SUMMARY_REPORT_OBJECT_TYPE } from "Common/dictionaries/summaryReportObjectType";
import { formatFractionToPercent } from "Common/digitUtils/format";
import { RedoButton, RefreshButton, SaveButton, UndoButton } from "Components/button";
import Menu from "Components/menu";
import ScrolledDropdown from "Components/scrolledDropdown";
import classNames from "classnames";
import map from "lodash/map";
import React, { memo, useId, useMemo } from "react";
import { MODE, MODE_ICONS, ZOOM, ZOOM_VALUES } from "../constants";
import locale from "./mapPanel.locale";
import styles from "Areas/Documents/graph/graphPanel/graphPanel.module.css";
import { Divider } from "Components/Divider";
import { ButtonWithDocumentPanel } from "Areas/SharedFeatures/downSidePanel";
const SIZES = {
    BTN_SIZE: 28,
    BTN_GROUP_HEIGHT: 28,
    BTN_DOCS: 32
};
const keySelector = (e) => e.key;
const textSelector = (e) => e.text;
const zoomItems = map(ZOOM_VALUES, (item, index) => ({
    key: item,
    text: index === 0
        ? `~${formatFractionToPercent(item / ZOOM.EQUIVALENT_100, 1)}`
        : formatFractionToPercent(item / ZOOM.EQUIVALENT_100, 1)
}));
const menuPosition = {
    my: "top left",
    at: "bottom left",
    collision: "fit flip"
};
const createGeometryMenuItemsLocale = {
    [MODE.VIEW]: undefined,
    [MODE.ADDING_LOCATION_POINT]: locale.ADD_LOCATION_POINT,
    [MODE.ADDING_LOCATION_LINE]: locale.ADD_LINE,
    [MODE.ADDING_LOCATION_AREA]: locale.ADD_AREA,
    [MODE.ADDING_LOCATION_POINT_ON_GRAPH]: locale.ADD_LOCATION_POINT_ON_GRAPH
};
const buildDeleteMenuItemRender = (icon, text, hint) => (React.createElement("div", { className: "grid-cols-auto-1fr with-gap-xs" },
    React.createElement("i", { className: classNames(icon, "font-h5") }),
    React.createElement("div", { className: "flex-space-between" },
        React.createElement("div", null, text),
        React.createElement("div", { className: "font-small text-muted pl-2" }, hint))));
function MapPanel(props) {
    const panelId = useId();
    const createGeometryMenuItems = useMemo(() => map([
        MODE.ADDING_LOCATION_POINT,
        MODE.ADDING_LOCATION_POINT_ON_GRAPH,
        MODE.ADDING_LOCATION_LINE,
        MODE.ADDING_LOCATION_AREA
    ], item => ({
        icon: classNames(MODE_ICONS[item], "font-h5", props.mode === item && "text-primary-600"),
        text: createGeometryMenuItemsLocale[item],
        onClick: () => {
            props.onChangeMode && props.onChangeMode(props.mode === item ? MODE.VIEW : item);
        },
        disabled: item === MODE.ADDING_LOCATION_POINT_ON_GRAPH && props.readOnlyMode === "readOnly"
    })), [props.onChangeMode, props.mode, props.readOnlyMode]);
    const deleteMenuItems = useMemo(() => [
        {
            icon: "icon-location-anchor-slash font-h5",
            text: locale.DELETE_FROM_MAP,
            onClick: () => {
                var _a;
                (_a = props.onDelete) === null || _a === void 0 ? void 0 : _a.call(props, true);
            },
            render: () => buildDeleteMenuItemRender("icon-location-anchor-slash", locale.DELETE_FROM_MAP, locale.DELETE_FROM_MAP_HINT)
        }
    ], [props.onDelete]);
    const summaryReportObject = useMemo(() => props.documentId
        ? {
            ...NEW_SUMMARY_REPORT_OBJECT,
            DocumentId: props.documentId,
            SummaryReportObjectType: SUMMARY_REPORT_OBJECT_TYPE.MAP_SCREEN
        }
        : undefined, [props.documentId]);
    return (React.createElement("div", { className: classNames(styles.wrapper, props.rightSidePanelOpen && styles["right-margin"]) },
        React.createElement("div", { className: classNames(styles.container, props.className), id: panelId },
            React.createElement("div", { className: styles.section },
                React.createElement(SaveButton, { onSave: props.onSave, disabled: !props.onSave, useNewBtn: true }),
                React.createElement(RefreshButton, { onRefresh: props.onRefresh, disabled: !props.onRefresh, useNewBtn: true, needConfirm: Boolean(props.onSave) })),
            React.createElement("div", { className: "vertical-divider" }),
            React.createElement("div", { className: styles.section },
                React.createElement(UndoButton, { onUndo: props.onUndo, disabled: !props.onUndo, useNewBtn: true }),
                React.createElement(RedoButton, { onRedo: props.onRedo, disabled: !props.onRedo, useNewBtn: true })),
            React.createElement("div", { className: "vertical-divider" }),
            React.createElement("div", { className: styles.section },
                React.createElement(Menu, { togglerClassName: "no-border btn-filled pt-0 pb-0", togglerTitle: locale.ADDING, togglerHeight: SIZES.BTN_SIZE, togglerWidth: "auto", togglerIconClassName: "icon-edit-map font-h5", items: createGeometryMenuItems, position: menuPosition, togglerHasRotatedArrow: true, selectedItemText: props.mode ? createGeometryMenuItemsLocale[props.mode] : undefined, disabled: !props.onChangeMode })),
            React.createElement("div", { className: "vertical-divider" }),
            React.createElement("div", { className: styles.section },
                React.createElement(Menu, { togglerClassName: "btn-filled pt-0 pb-0", togglerTitle: locale.DELETING, togglerHeight: SIZES.BTN_SIZE, togglerWidth: "auto", togglerIconClassName: "icon-trash-2 font-h5", items: deleteMenuItems, position: menuPosition, togglerHasRotatedArrow: true, disabled: !props.onDelete })),
            React.createElement("div", { className: "vertical-divider" }),
            React.createElement("div", { className: styles.section },
                React.createElement(TaskResultLimitPanel, null)),
            props.projectId && summaryReportObject && props.canAddToSummaryReports && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "vertical-divider" }),
                React.createElement("div", { className: styles.section },
                    React.createElement(AddToSummaryReport, { height: SIZES.BTN_SIZE, data: summaryReportObject, projectId: props.projectId, getValue: props.getSummaryReportObjectValue })))),
            React.createElement("div", { className: "vertical-divider" }),
            React.createElement("div", { className: styles.section },
                React.createElement(ScrolledDropdown, { keySelector: keySelector, textSelector: textSelector, selectedKey: props.zoom, onSelectedChanged: props.onChangeZoom, items: zoomItems, togglerClassName: "no-border bg-transparent p-0", togglerHeight: SIZES.BTN_SIZE, togglerTitle: locale.ZOOM, minWidth: 75, maxHeight: 300, caption: props.zoom ? formatFractionToPercent(props.zoom / ZOOM.EQUIVALENT_100, 1) : "", togglerMuteDefaultCaptionText: false })),
            React.createElement(Divider, { direction: "vertical" }),
            React.createElement("div", { className: styles.section },
                React.createElement(ButtonWithDocumentPanel, { targetId: panelId })))));
}
export default memo(MapPanel);
